/**
 * "Cancel" for one live task, in the phone calendar's sheet.
 *
 * NO RULE OF ITS OWN. Whether Cancel is offered at all is
 * `useTaskControlGates` -- the very hook `TaskControlActions` (the task
 * drawer's controls) reads: the same roles (`TASK_CONTROL_ROLES`), the
 * same `task.cancel` capability from the site's capability matrix, the
 * same status-and-ownership rule, and the same stand-down while the
 * robot is in or boarding a lift car. What the phone adds is a confirm
 * step (a thumb on a moving phone presses things) and the drawer's own
 * verdict words afterwards (`taskControlOutcome`), so a cancel that
 * nothing answered never reads as done.
 *
 * View-as-role preview draws the button but switches it off, as on every
 * other action screen (`useActionsOff`).
 */
import { useState } from 'react';
import { useTaskControl } from '../../../api/hooks';
import { hasAnyRole } from '../../../auth/roles';
import { useAuth } from '../../../auth/useAuth';
import { useActionsOff } from '../../../auth/useRolePreview';
import type { RobotStateView, TaskState } from '../../../api/rmf-client';
import type { LiftViewModel } from '../../../api/adapters';
import {
  CONTROL_ACTIONS,
  TASK_CONTROL_ROLES,
  taskControlOutcome,
  useTaskControlGates,
} from '../task-control-gates';
import { TASK_VERDICT_CODE_TEXT } from '../task-command-verdict';

const CANCEL = CONTROL_ACTIONS.find((a) => a.action === 'cancel')!;

export function PhoneTaskCancel({
  task,
  robot,
  lifts,
}: {
  task: TaskState;
  /** As `TaskControlActions` takes them: omitted, they are looked up. */
  robot?: RobotStateView;
  lifts?: readonly LiftViewModel[];
}) {
  const { roles, isAuthenticated } = useAuth();
  const actionsOff = useActionsOff();
  const control = useTaskControl();
  const gates = useTaskControlGates(task, robot, lifts);
  const [confirming, setConfirming] = useState(false);
  const outcome = taskControlOutcome(control, task.id);

  if (!isAuthenticated || !hasAnyRole(roles, TASK_CONTROL_ROLES)) return null;

  const offered = gates.availableActions.some((a) => a.action === 'cancel');
  const cap = gates.capabilityMap.get(CANCEL.capability);
  const capOff = gates.capabilitiesLoaded && cap?.enabled === false;

  return (
    <div className="nx-pcal-cancel" data-testid="pcal-cancel">
      {gates.guardedForLift && (
        <p className="nx-pcal-note is-fail" role="alert" data-testid="pcal-cancel-in-lift">
          Don’t cancel while it is in or at {gates.inLift?.name ?? 'the lift'}. Call Nexsoss.
        </p>
      )}
      {offered && !gates.guardedForLift && !confirming && (
        <button
          type="button"
          className="nx-pcal-btn nx-pcal-btn--danger"
          onClick={() => setConfirming(true)}
          disabled={actionsOff || capOff || control.isPending}
          title={
            actionsOff
              ? 'Viewing as another role: look only.'
              : capOff
                ? (cap?.disabled_reasons ?? []).join(', ')
                : CANCEL.hint
          }
          data-testid="pcal-cancel-btn"
        >
          {control.isPending ? 'Sending…' : 'Cancel'}
        </button>
      )}
      {offered && !gates.guardedForLift && confirming && (
        <div className="nx-pcal-confirm" role="group" aria-label="Confirm cancel">
          <p className="nx-pcal-confirm__q">
            Cancel this task? The robot may finish a step first (for example, back out of a
            doorway) before it stops.
          </p>
          <div className="nx-pcal-confirm__row">
            <button
              type="button"
              className="nx-pcal-btn"
              onClick={() => setConfirming(false)}
              data-testid="pcal-cancel-keep"
            >
              Keep it
            </button>
            <button
              type="button"
              className="nx-pcal-btn nx-pcal-btn--danger"
              disabled={actionsOff || capOff || control.isPending}
              onClick={() => {
                setConfirming(false);
                control.mutate({ taskId: task.id, action: 'cancel' });
              }}
              data-testid="pcal-cancel-confirm"
            >
              Cancel task
            </button>
          </div>
        </div>
      )}
      {outcome?.kind === 'pending' && (
        <p className="nx-pcal-note" role="status" data-testid="pcal-cancel-pending">
          Not confirmed yet. {outcome.message}
        </p>
      )}
      {outcome?.kind === 'verdict' && (
        <p
          className={`nx-pcal-note${outcome.status === 'done' ? ' is-ok' : ' is-fail'}`}
          role={outcome.status === 'done' ? 'status' : 'alert'}
          data-testid="pcal-cancel-verdict"
          data-status={outcome.status}
        >
          {outcome.status === 'done'
            ? 'Cancelled.'
            : (outcome.code && TASK_VERDICT_CODE_TEXT[outcome.code]) || outcome.reason}
        </p>
      )}
    </div>
  );
}
